import type { SheetBinder } from "./sheet-index-renderer.ts";
import { DomFlags } from "@tento-ui/dom-flags";

/**
 * Why each column's next full bind runs (F10), as the sheet element knows it.
 *
 * The scroll engine coalesces bind requests per column, so a pass cannot say
 * which request caused it. This ledger answers per column instead: a pass is
 * `"membership"` only when a row-viewport membership change was requested
 * since the column's last full bind AND no content change was (a data
 * rebind, a sheet bind, a view-model swap), AND the column is bound to the
 * same sheet geometry (sheet, layout, rows) and width as last time.
 * Everything else, including a column's first bind, is `"content"`. Content
 * requests are recorded for every column at once: conservative (a one-column
 * data rebind makes every column's next membership pass a content pass), never
 * wrong.
 */
export class BindReasonLedger {
  /**
   * @param membershipPasses whether membership-only passes are reported at all
   *   (`membershipPassesEnabled()`); false reports every pass as content (the
   *   pre-F10 behaviour, for an A/B measurement in one build).
   */
  constructor(readonly membershipPasses: boolean) {}

  #tick = 0;
  #membershipAt = 0;
  #contentAt = 0;
  readonly #bound = new Map<number, { readonly tick: number; readonly geometry: readonly object[]; readonly widthPx: number }>();

  /** The row viewport's membership changed (every column). */
  membershipChanged(): void {
    this.#membershipAt = ++this.#tick;
  }

  /** Content may have changed for every column (data, sheet, view model). */
  contentChanged(): void {
    this.#contentAt = ++this.#tick;
  }

  /** Forget every column's last bind (a recycle or detach: the next binds are content). */
  reset(): void {
    this.#bound.clear();
    this.contentChanged();
  }

  /**
   * The reason for the full bind of column `index` that is about to run, with
   * the sheet `geometry` it binds against (compared by identity) and its
   * width; records the bind.
   */
  reasonFor(index: number, geometry: readonly object[], widthPx: number): SheetBinder.PassReason {
    const last = this.#bound.get(index);
    const tick = ++this.#tick;
    this.#bound.set(index, { tick, geometry, widthPx });
    if (last == null) return "content";
    if (this.#contentAt > last.tick) return "content";
    if (this.#membershipAt <= last.tick) return "content";
    if (last.widthPx !== widthPx || last.geometry.length !== geometry.length) return "content";
    for (const [i, part] of geometry.entries()) if (last.geometry[i] !== part) return "content";
    return this.membershipPasses ? "membership" : "content";
  }
}

/**
 * Whether membership-only passes are on (F10), from the `row-membership-pass`
 * flag (`DomFlags`): on unless `?flag:row-membership-pass=false` (or
 * localStorage `debug-flag:row-membership-pass=false`). Read when a sheet
 * element is created.
 */
export function membershipPassesEnabled(): boolean {
  return DomFlags.read("row-membership-pass", true);
}
